import React from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader,
  Typography, 
  Chip, 
  Box, 
  Stack,
  IconButton,
  Grid
} from '@mui/material';
import { 
  ConfirmationNumber as Ticket, 
  CalendarMonth as Calendar, 
  Edit, 
  Delete as Trash2 
} from '@mui/icons-material';
import { SkiPass } from '@/types';
import AddSkiPassForm from '@/components/skipass/AddSkiPassForm';
import { useI18n } from '@/hooks/useI18n';

interface SkiPassCardProps {
  skiPass: SkiPass;
  onUpdate: (skiPass: SkiPass) => void;
  onDelete: (skiPassId: string) => void;
}

const SkiPassCard: React.FC<SkiPassCardProps> = ({ skiPass, onUpdate, onDelete }) => {
  const { t } = useI18n();
  
  const getTypeBadgeColor = (type: string): 'warning' | 'primary' | 'secondary' | 'default' => {
    switch (type) {
      case 'daily':
        return 'warning';
      case 'multi-day':
        return 'primary';
      case 'season':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type: string): string => {
    switch (type) {
      case 'daily':
        return t('skipass.form.typeDaily');
      case 'multi-day':
        return t('skipass.form.typeMultiDay');
      case 'season':
        return t('skipass.form.typeSeason');
      default:
        return type;
    }
  };

  return (
    <Card sx={{ 
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      transition: 'box-shadow 0.3s ease-in-out',
      '&:hover': {
        boxShadow: (theme) => theme.shadows[8]
      }
    }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Ticket sx={{ color: 'primary.main' }} />
            {skiPass.name}
          </Box>
        }
        action={
          <Stack direction="row" spacing={1} alignItems="center" sx={{ pt:1 }}>
            <Chip 
              label={getTypeLabel(skiPass.type)}
              color={getTypeBadgeColor(skiPass.type)}
              size="small"
            />
            <AddSkiPassForm
              editingSkiPass={skiPass}
              onUpdateSkiPass={onUpdate}
              trigger={
                <IconButton size="small">
                  <Edit sx={{ fontSize: 16 }} />
                </IconButton>
              }
            />
            <IconButton 
              size="small" 
              color="error"
              onClick={() => onDelete(skiPass.id)}
            >
              <Trash2 sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        }
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ flexGrow: 1, pt: 0 }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
            <Calendar sx={{ fontSize: 16 }} />
            <Typography variant="body2">
              {t('skipass.form.validDays')}: {skiPass.validDays} {t('days', { count: skiPass.validDays })}
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" fontWeight="medium" color="text.primary" sx={{ mb: 1.5 }}>
              {t('common.pricing')} (€):
            </Typography>
            
            <Grid container spacing={1.5}>
              <Grid size={6}>
              <Box sx={{ 
                bgcolor: 'success.50', 
                p: 1.5, 
                borderRadius: 1,
                border: 1,
                borderColor: 'success.200'
              }}>
                <Typography variant="body2" fontWeight="medium" color="success.main">
                  {t('skipass.children')} (&lt;12)
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="success.main">
                  {skiPass.childPrice}
                </Typography>
              </Box>
              </Grid>

              <Grid size={6}>
              <Box sx={{ 
                bgcolor: 'primary.50', 
                p: 1.5, 
                borderRadius: 1,
                border: 1,
                borderColor: 'primary.200'
              }}>
                <Typography variant="body2" fontWeight="medium" color="primary.main">
                  {t('skipass.youth')} (&lt;18)
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  {skiPass.youthPrice}
                </Typography>
              </Box>
              </Grid>
              <Grid size={6}>
              <Box sx={{ 
                bgcolor: 'grey.50', 
                p: 1.5, 
                borderRadius: 1,
                border: 1,
                borderColor: 'grey.200'
              }}>
                <Typography variant="body2" fontWeight="medium" color="text.primary">
                  {t('skipass.adults')}
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                  {skiPass.adultPrice}
                </Typography>
              </Box>
              </Grid>
              
              <Grid size={6}>
              <Box sx={{ 
                bgcolor: 'secondary.50', 
                p: 1.5, 
                borderRadius: 1,
                border: 1,
                borderColor: 'secondary.200'
              }}>
                <Typography variant="body2" fontWeight="medium" color="secondary.main">
                  {t('skipass.seniors')} (65+)
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="secondary.main">
                  {skiPass.seniorPrice}
                </Typography>
              </Box>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              {t('skipass.agePricingNote')}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default SkiPassCard;
